// Sidebar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FaArchive,
  FaHome,
  FaInbox,
  FaEnvelope,
  FaPaperPlane,
  FaProjectDiagram,
  FaUsers,
  FaUserFriends,
  FaBullhorn,
} from "react-icons/fa";
import { SidebarItem } from "../molecules/SidebarItem";
import MTNLogo from "../../../../assets/logo/JPEG/MTN_2022_Logo_SolidBG_Black_Yellow_RGB.jpg";
import { API_BASE_URL } from "../../../config/api";

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}
function norm(v) {
  return String(v || "").trim().toLowerCase();
}
function fromGroups(groups) {
  if (!groups) return "";
  const arr = Array.isArray(groups) ? groups : Object.values(groups || {});
  const names = arr.map((g) => norm(g?.cn || g?.name || g));
  if (names.some((n) => n.includes("admin"))) return "admin";
  return "";
}
function getUserRole() {
  const direct = norm(localStorage.getItem("role"));
  if (direct) return direct;

  const user = safeParse(localStorage.getItem("user"));
  if (user?.role) return norm(user.role);
  if (user?.groups) {
    const g = fromGroups(user.groups);
    if (g) return g;
  }

  const session = safeParse(localStorage.getItem("session"));
  if (session?.role) return norm(session.role);
  if (session?.user?.role) return norm(session.user.role);
  if (session?.user?.groups) {
    const g = fromGroups(session.user.groups);
    if (g) return g;
  }

  const meille = safeParse(localStorage.getItem("Umwe_Kumubumbe"));
  if (meille?.role) return norm(meille.role);
  if (meille?.user?.role) return norm(meille.user.role);
  if (meille?.user?.groups) {
    const g = fromGroups(meille.user.groups);
    if (g) return g;
  }

  const profile = safeParse(localStorage.getItem("profile"));
  if (profile?.role) return norm(profile.role);
  if (profile?.groups) {
    const g = fromGroups(profile.groups);
    if (g) return g;
  }

  const rawJwt =
    localStorage.getItem("access_token") || localStorage.getItem("token");
  if (rawJwt && rawJwt.split(".").length === 3) {
    try {
      const payload = JSON.parse(atob(rawJwt.split(".")[1]));
      if (payload?.role) return norm(payload.role);
      const g = fromGroups(payload?.groups);
      if (g) return g;
    } catch {
      /* ignore */
    }
  }

  return "";
}

export const Sidebar = () => {
  const location = useLocation();
  const [activeLabel, setActiveLabel] = useState("Overview");
  const [isWorkflowOpen, setWorkflowOpen] = useState(false);
  const [role, setRole] = useState(getUserRole());
  const [permissions, setPermissions] = useState([]);
  const [accountRole, setAccountRole] = useState("");

  
  const isHovered = true;

  useEffect(() => {
    const onStorage = () => setRole(getUserRole());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    // Role rarely changes mid-session; avoid a 1.5s poll that keeps re-rendering the shell.
    const timer = setInterval(() => setRole(getUserRole()), 60000);
    return () => clearInterval(timer);
  }, []);

  const isAdmin = useMemo(
    () => role.includes("admin") || norm(accountRole).includes("admin"),
    [role, accountRole]
  );
  const isSuperAdmin = useMemo(() => {
    const value = norm(accountRole);
    return value === "super admin" || value === "superadmin";
  }, [accountRole]);
  const can = (key) => isAdmin || permissions.includes(key);
  const showWorkflow =
    can("requests") || can("jobs") || can("marks") || can("exam");

  useEffect(() => {
    if (location.pathname.startsWith("/advertise")) setActiveLabel("Advertise");
    if (location.pathname.startsWith("/users")) setActiveLabel("Users");
    if (location.pathname.startsWith("/emails")) setActiveLabel("Emails");
    if (location.pathname.startsWith("/subscribers")) setActiveLabel("Subscribers");
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        if (Array.isArray(data.permissions)) setPermissions(data.permissions);
        if (data.role) setAccountRole(String(data.role));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [role]);

  const handleSetActive = (label) => {
    setActiveLabel(label);
    if (label !== "Workflow") setWorkflowOpen(false);
  };

  return (
    <div
      className="h-full p-4 text-white bg-black transition-all duration-300 w-64"
      data-role={role || "unknown"}
    >
      <img
        src={MTNLogo}
        alt="MTN Logo"
        className="mx-auto mb-4 transition-all duration-300 w-20 h-20"
      />

      <SidebarItem
        icon={FaHome}
        label="Overview"
        to="/dashboard"
        active={activeLabel === "Overview"}
        onClick={() => handleSetActive("Overview")}
        isHovered={isHovered}
      />

      <SidebarItem
        icon={FaInbox}
        label="Inbox"
        to="/inbox"
        active={activeLabel === "Inbox"}
        onClick={() => handleSetActive("Inbox")}
        isHovered={isHovered}
      />

      <SidebarItem
        icon={FaPaperPlane}
        label="Sent"
        to="/sent"
        active={activeLabel === "Sent"}
        onClick={() => handleSetActive("Sent")}
        isHovered={isHovered}
      />

    
      {showWorkflow && (
        <SidebarItem
          icon={FaProjectDiagram}
          label="Workflow"
          active={activeLabel === "Workflow"}
          onClick={() => handleSetActive("Workflow")}
          isOpen={isWorkflowOpen}
          setIsOpen={setWorkflowOpen}
          isHovered={isHovered}
          canOpenChildren={showWorkflow}
        >
          {can("requests") && (
            <SidebarItem
              label="Requests"
              to="/tables"
              active={activeLabel === "tables"}
              onClick={() => handleSetActive("tables")}
              isHovered={isHovered}
            />
          )}

          {can("jobs") && (
            <SidebarItem
              icon={FaBullhorn}
              label="Advertise"
              to="/advertise"
              active={activeLabel === "Advertise"}
              onClick={() => handleSetActive("Advertise")}
              isHovered={isHovered}
            />
          )}

          {can("jobs") && (
            <SidebarItem
              label="Jobs"
              to="/candidate"
              active={activeLabel === "Jobs"}
              onClick={() => handleSetActive("Jobs")}
              isHovered={isHovered}
            />
          )}

          {can("marks") && (
            <SidebarItem
              label="Marks"
              to="/marks"
              active={activeLabel === "marks"}
              onClick={() => handleSetActive("marks")}
              isHovered={isHovered}
            />
          )}

          {can("exam") && (
            <SidebarItem
              label="Exam"
              to="/exam"
              active={activeLabel === "exam"}
              onClick={() => handleSetActive("exam")}
              isHovered={isHovered}
            />
          )}
        </SidebarItem>
      )}

      {isSuperAdmin && (
        <SidebarItem
          icon={FaEnvelope}
          label="Emails"
          to="/emails"
          active={activeLabel === "Emails"}
          onClick={() => handleSetActive("Emails")}
          isHovered={isHovered}
        />
      )}

      {isSuperAdmin && (
        <SidebarItem
          icon={FaUserFriends}
          label="Subscribers"
          to="/subscribers"
          active={activeLabel === "Subscribers"}
          onClick={() => handleSetActive("Subscribers")}
          isHovered={isHovered}
        />
      )}

      {isSuperAdmin && (
        <SidebarItem
          icon={FaUsers}
          label="Users"
          to="/users"
          active={activeLabel === "Users"}
          onClick={() => handleSetActive("Users")}
          isHovered={isHovered}
        />
      )}

      <SidebarItem
        icon={FaArchive}
        label="Archive"
        to="/archive"
        active={activeLabel === "Archive"}
        onClick={() => handleSetActive("Archive")}
        isHovered={isHovered}
      />
    </div>
  );
};
